import { useMutation, useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { useApp } from '@/app/AppContext'
import { can } from '@/auth/permissions'
import { Icon } from '@/components/Icon'
import {
  ConfirmModal,
  Facts,
  Panel,
  RecordHeader,
  RecordTabs,
  StageBar,
  StateView,
  type Fact,
  type HeaderAction,
  type MenuAction,
} from './record'
import { CopyValue, labelOf, listPath } from './recordHelpers'
import type { RecordViewProps } from './types'
import {
  cancelTransfer,
  completeTransferReceipt,
  dispatchTransfer,
  fetchTransfer,
  fetchTransferTerminal,
  transitLoss,
  type Transfer,
  type TransferLine,
  type TransitLossLine,
} from './warehouseApi'
import {
  CellCode,
  HistoryTab,
  OperationAlerts,
  ProductCell,
  RowFilter,
  StatusBadge,
  cancelledEnd,
  copyLinkItem,
  documentState,
  momentOr,
  newOperationKey,
  qty,
  short,
  signedQty,
  stagesOf,
  sum,
  useOperation,
} from './warehouseRecord'
import '@/styles/record.css'

const STAGES = ['draft', 'in_transit', 'partial', 'received'] as const

/**
 * Карточка перемещения между складами на дизайн-системе (Transfer.dc.html).
 *
 * Потеря в пути — **отдельный факт**, а не недостача склада-получателя: списать её молча
 * значит спрятать вопрос к перевозчику. Поэтому строка несёт отправленное, полученное и
 * признак «строку смотрели» (`counted`, V15) — ноль полученного без признака неотличим от
 * «не смотрели», а это разные вещи: претензия и незаконченная работа. Непосчитанная строка
 * показывает «ожидает приёмки», а не ноль и не потерю.
 *
 * Действия в шапке по статусу: черновик отправляют (подтверждение: отправка списывает
 * товар с остатка отправителя), перемещение в пути принимают — «Завершить приёмку»
 * (`/wms/transfers/{id}/complete`) под `warehouse.transfer:update`, тем же правом, которым
 * кладовщик принимает перемещение на ТСД (#80). Строки пересчитывают на терминале
 * получателя; пока хоть одна не посчитана, домен откажет, и кнопка говорит об этом заранее.
 * Отмена — только черновика, в «⋯» через подтверждение.
 *
 * ## Чего здесь нет, хотя макет это рисует
 *
 * - Акт о потерях как документ (номер, подпись перевозчика, претензия в бухгалтерию),
 *   суммы по учётной цене, перевозчик, рейс, пломба, накладная, основание-заявка,
 *   плановый срок: API этого не отдаёт. Потери показаны по строкам, как их считает сервер.
 * - «Принять остаток» с пересчётом в офисе, «Добавить товары», «Печать накладной», отмена
 *   непринятого остатка: пересчёт — работа терминала, остальных операций нет.
 * - Лента событий и комментарии: единая лента (#79) складские документы не знает.
 */
export function TransferRecordView({ recordId, record, form, notices }: RecordViewProps) {
  const { t, user, locale } = useApp()
  const [tab, setTab] = useState('lines')
  const [filter, setFilter] = useState<'all' | 'waiting' | 'loss'>('all')
  const [confirm, setConfirm] = useState<'dispatch' | 'cancel' | null>(null)
  const [losses, setLosses] = useState<TransitLossLine[] | null>(null)
  const operation = useOperation([
    ['transfer', recordId],
    ['transfer-terminal', recordId],
    ['record', 'warehouse.transfer', recordId],
    ['list', 'warehouse.transfer'],
  ])

  const mayUpdate = can(user, 'warehouse.transfer', 'update')
  const mayCancel = can(user, 'warehouse.transfer', 'delete')

  const transferQuery = useQuery({
    queryKey: ['transfer', recordId],
    queryFn: ({ signal }) => fetchTransfer(recordId, signal),
    retry: false,
  })
  const terminal = useQuery({
    queryKey: ['transfer-terminal', recordId],
    queryFn: ({ signal }) => fetchTransferTerminal(recordId, signal),
    retry: false,
  })

  const dispatch = useMutation({
    mutationFn: () => dispatchTransfer(recordId),
    onSuccess: () => {
      setConfirm(null)
      operation.succeed(t('card.wh.transfer.dispatchedDone'))
    },
    onError: (error) => {
      setConfirm(null)
      operation.fail(error)
    },
  })
  const complete = useMutation({
    mutationFn: (key: string) => completeTransferReceipt(recordId, key),
    onSuccess: (result) => {
      setLosses(result)
      operation.succeed(
        result.length > 0 ? t('record.wh.tr.completedLoss', { count: result.length }) : t('record.wh.tr.completed'),
      )
    },
    onError: operation.fail,
  })
  const cancel = useMutation({
    mutationFn: () => cancelTransfer(recordId),
    onSuccess: () => {
      setConfirm(null)
      operation.succeed(t('card.wh.transfer.cancelledDone'))
    },
    onError: (error) => {
      setConfirm(null)
      operation.fail(error)
    },
  })

  const gate = documentState(transferQuery, 'warehouse.transfer', '/transfers', t)
  if (gate) return gate
  const transfer = transferQuery.data as Transfer

  const names = terminal.data?.catalogue?.names ?? {}
  const lines = transfer.lines
  const countedLines = lines.filter((line) => line.counted)
  const waiting = lines.filter((line) => !line.counted)
  const lossy = lines.filter((line) => transitLoss(line) > 0)
  const draft = transfer.status === 'draft'
  const inTransit = transfer.status === 'in_transit'
  const partial = inTransit && countedLines.length > 0

  // ------------------------------------------------------------------ шапка

  const from = labelOf(record.values, 'from_warehouse_id') ?? short(transfer.fromWarehouseId)
  const to = labelOf(record.values, 'to_warehouse_id') ?? short(transfer.toWarehouseId)
  const meta = [
    <StatusBadge key="status" entity="warehouse.transfer" status={transfer.status} />,
    transfer.hasTransitLoss ? (
      <span key="loss" className="badge b-danger">
        <Icon name="alert" />
        {t('record.wh.tr.lossBadge')}
      </span>
    ) : null,
    <span key="dispatched">{t('record.wh.tr.dispatchedMeta', { at: momentOr(transfer.dispatchedAt, locale, t('card.wh.transfer.notDispatched')) })}</span>,
    <CopyValue key="copy" value={transfer.number} label={t('record.copyNumber', { number: transfer.number })} />,
  ]

  let primary: HeaderAction | null = null
  if (mayUpdate && draft) {
    primary = {
      id: 'dispatch',
      label: t('card.wh.transfer.dispatchSubmit'),
      icon: 'truck',
      disabled: lines.length === 0 || dispatch.isPending,
      onClick: () => {
        operation.clear()
        setConfirm('dispatch')
      },
    }
  } else if (mayUpdate && inTransit) {
    // Counting happens on the receiver's terminal; until every line is counted the domain
    // refuses to close, so the button says so beforehand.
    primary = {
      id: 'complete',
      label: t('record.wh.tr.complete'),
      icon: 'check',
      disabled: waiting.length > 0 || complete.isPending,
      onClick: () => {
        operation.clear()
        complete.mutate(newOperationKey())
      },
    }
  }

  const menu: MenuAction[] = [copyLinkItem(t, operation.setFailure)]
  if (mayCancel && draft) {
    menu.push({
      id: 'cancel',
      label: t('card.wh.transfer.cancel'),
      icon: 'close',
      danger: true,
      onSelect: () => {
        operation.clear()
        setConfirm('cancel')
      },
    })
  }

  // ------------------------------------------------------------------ этапы и факты

  const reached =
    transfer.status === 'received' ? 3 : partial ? 2 : inTransit ? 1 : draft ? 0 : -1
  const stageMeta = (code: string) => {
    switch (code) {
      case 'draft':
        return momentOr(record.values.created_at, locale, '')
      case 'in_transit':
        return momentOr(transfer.dispatchedAt, locale, t('card.wh.transfer.notDispatched'))
      case 'partial':
        return partial ? t('record.wh.of', { done: countedLines.length, total: lines.length }) : ''
      default:
        return momentOr(transfer.receivedAt, locale, t('card.wh.transfer.notReceived'))
    }
  }

  const sent = sum(lines, (line) => line.quantity)
  const received = sum(countedLines, (line) => line.received)
  const lost = sum(lines, transitLoss)
  const travelEnd = transfer.receivedAt ? new Date(transfer.receivedAt) : new Date()
  const travelDays = transfer.dispatchedAt
    ? Math.max(0, Math.floor((travelEnd.getTime() - new Date(transfer.dispatchedAt).getTime()) / 86_400_000))
    : null
  const facts: Fact[] = [
    {
      id: 'sent',
      label: t('card.wh.transfer.sent'),
      value: t('record.wh.pieces', { qty: qty(sent, locale) }),
      sub: t('record.wh.tr.positions', { count: lines.length }),
    },
    {
      id: 'received',
      label: t('record.wh.tr.received'),
      value: countedLines.length > 0 ? t('record.wh.pieces', { qty: qty(received, locale) }) : '—',
      sub:
        waiting.length > 0 && !draft
          ? t('record.wh.tr.waitingSub', { qty: qty(sum(waiting, (line) => line.quantity), locale) })
          : draft
            ? t('record.wh.tr.afterDispatch')
            : undefined,
    },
    {
      id: 'loss',
      label: t('card.wh.transfer.loss'),
      value:
        lost > 0 ? (
          <span style={{ color: 'var(--danger-text)' }}>{t('record.wh.pieces', { qty: qty(lost, locale) })}</span>
        ) : (
          <span className="muted">{countedLines.length > 0 ? '0' : '—'}</span>
        ),
      sub:
        lost > 0 ? (
          <span className="flag flag-danger">
            <Icon name="alert" />
            {t('record.wh.tr.lossSub', { count: lossy.length })}
          </span>
        ) : (
          t('record.wh.tr.lossOnReceipt')
        ),
    },
    {
      id: 'travel',
      label: t('record.wh.tr.travel'),
      value: travelDays == null ? '—' : t('record.wh.days', { days: travelDays }),
      sub: transfer.dispatchedAt
        ? `${momentOr(transfer.dispatchedAt, locale, '')} → ${momentOr(transfer.receivedAt, locale, t('record.wh.tr.onTheWay'))}`
        : undefined,
    },
  ]

  // ------------------------------------------------------------------ вкладки

  const visible = lines.filter((line) =>
    filter === 'waiting' ? !line.counted : filter === 'loss' ? transitLoss(line) > 0 : true,
  )
  const linesPanel =
    lines.length === 0 ? (
      <StateView boxed kind="empty" title={t('card.wh.transfer.noLines')} description={t('record.wh.tr.emptyText')} />
    ) : (
      <Panel
        title={t('record.wh.tr.lines')}
        count={lines.length}
        flush
        actions={
          <RowFilter
            label={t('record.wh.rowFilter')}
            value={filter}
            onChange={setFilter}
            options={[
              { id: 'all', label: t('record.wh.filter.all') },
              { id: 'waiting', label: t('record.wh.tr.filterWaiting'), count: draft ? 0 : waiting.length },
              { id: 'loss', label: t('record.wh.tr.filterLoss'), count: lossy.length },
            ]}
          />
        }
      >
        <div style={{ overflowX: 'auto' }}>
          <table className="tbl" aria-label={t('record.wh.tr.lines')}>
            <thead>
              <tr>
                <th>{t('card.wh.line.product')}</th>
                <th className="num">{t('card.wh.transfer.sent')}</th>
                <th className="num">{t('record.wh.tr.received')}</th>
                <th className="num">{t('card.wh.transfer.loss')}</th>
                <th>{t('record.wh.tr.toCell', { warehouse: to })}</th>
                <th>{t('record.wh.tr.lineStatus')}</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((line) => (
                <TransferRow key={line.id} line={line} draft={draft} name={line.productName ?? names[line.productId]} />
              ))}
              <tr className="total">
                <td>{t('record.wh.tr.total', { count: lines.length })}</td>
                <td className="num">{qty(sent, locale)}</td>
                <td className="num">{countedLines.length > 0 ? qty(received, locale) : '—'}</td>
                <td className="num">{lost > 0 ? <span style={{ color: 'var(--danger-text)' }}>{qty(lost, locale)}</span> : '—'}</td>
                <td />
                <td style={{ fontWeight: 400 }}>
                  {draft ? '' : t('record.wh.tr.totalStatus', { counted: countedLines.length, total: lines.length })}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        {/* Receiving is done on the receiver's terminal: the boxes are counted there, and the
            loss shows up there, not in the sender's office. */}
        <div className="panel-f secondary">{t('card.wh.transfer.receiptOnTerminal')}</div>
      </Panel>
    )

  const lossPanel = (
    <Panel title={t('record.wh.tr.lossPanel')} count={(losses ?? lossy).length} flush>
      {losses != null ? (
        losses.length === 0 ? (
          <div className="panel-b secondary">{t('record.wh.tr.noLossFinal')}</div>
        ) : (
          <table className="tbl tbl-plain" aria-label={t('record.wh.tr.lossPanel')}>
            <thead>
              <tr>
                <th>{t('card.wh.line.product')}</th>
                <th className="num">{t('card.wh.transfer.sent')}</th>
                <th className="num">{t('record.wh.tr.received')}</th>
                <th className="num">{t('card.wh.transfer.loss')}</th>
              </tr>
            </thead>
            <tbody>
              {losses.map((row, index) => (
                <tr key={`${row.productId}-${index}`}>
                  <td className="wrap">{names[row.productId] ?? short(row.productId)}</td>
                  <td className="num">{qty(row.sent, locale)}</td>
                  <td className="num">{qty(row.received, locale)}</td>
                  <td className="num strong">{qty(row.shortfall, locale)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )
      ) : lossy.length === 0 ? (
        <div className="panel-b secondary">{countedLines.length > 0 ? t('record.wh.tr.noLossYet') : t('record.wh.tr.lossOnReceipt')}</div>
      ) : (
        <table className="tbl tbl-plain" aria-label={t('record.wh.tr.lossPanel')}>
          <thead>
            <tr>
              <th>{t('card.wh.line.product')}</th>
              <th className="num">{t('record.wh.tr.pieces')}</th>
            </tr>
          </thead>
          <tbody>
            {lossy.map((line) => (
              <tr key={line.id}>
                <td className="wrap">{line.productName ?? names[line.productId] ?? short(line.productId)}</td>
                <td className="num strong">{qty(transitLoss(line), locale)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <div className="panel-f secondary">{t('record.wh.tr.actMissing')}</div>
    </Panel>
  )

  return (
    <div className="rec-page">
      <RecordHeader
        crumbs={[
          { label: t('nav.transfers'), to: listPath('warehouse.transfer', '/transfers') },
          { label: transfer.number, mono: true },
        ]}
        title={
          <span className="wh-route-title">
            <span>{t('record.wh.tr.title')}</span>
            <span className="tag">
              <Icon name="box" size="sm" />
              {from}
            </span>
            <Icon name="arrow-right" />
            <span className="tag">
              <Icon name="box" size="sm" />
              {to}
            </span>
          </span>
        }
        meta={meta}
        primary={primary}
        menu={menu}
        menuLabel={t('record.more')}
      />

      <OperationAlerts failure={operation.failure} notice={operation.notice} />
      {notices}
      {transfer.hasTransitLoss && (
        // Named separately and loudly: it is a claim against the carrier, and writing it off
        // as the receiver's shortage would close the question without asking it.
        <div className="alert alert-danger" role="alert">
          <Icon name="alert" />
          <span>{t('card.wh.transfer.lossNotice')}</span>
        </div>
      )}
      {primary?.id === 'complete' && waiting.length > 0 && (
        <div className="alert alert-neutral" role="status">
          <Icon name="clock" />
          <span>{t('record.wh.tr.completeBlocked', { count: waiting.length })}</span>
        </div>
      )}

      <StageBar
        ariaLabel={t('record.wh.stages')}
        steps={stagesOf(STAGES, reached, (code) => t(`record.wh.tr.stage.${code}`), stageMeta)}
        ends={cancelledEnd(transfer.status === 'cancelled', t('record.wh.cancelledEnd'))}
      />

      <Facts items={facts} ariaLabel={t('record.facts')} />

      <RecordTabs
        tabs={[
          { id: 'lines', label: t('card.tab.whTransferLines'), count: lines.length },
          { id: 'history', label: t('record.tab.history') },
        ]}
        active={tab}
        onChange={setTab}
        ariaLabel={t('record.wh.tr.sections')}
      >
        {tab === 'history' ? (
          <HistoryTab entity="warehouse.transfer" recordId={recordId} feedType="stock_transfer" />
        ) : (
          <div className="layout-2-wide" style={{ paddingTop: 14 }}>
            <div className="rec-col">{linesPanel}</div>
            <div className="rec-col">
              {!draft && lossPanel}
              <Panel title={t('record.wh.details')}>{form}</Panel>
            </div>
          </div>
        )}
      </RecordTabs>

      <ConfirmModal
        open={confirm === 'dispatch'}
        icon="truck"
        title={t('record.wh.tr.dispatch.title', { number: transfer.number })}
        confirmLabel={t('card.wh.transfer.dispatchSubmit')}
        pending={dispatch.isPending}
        onCancel={() => setConfirm(null)}
        onConfirm={() => dispatch.mutate()}
      >
        {/* What dispatch does is said before the click: it writes the goods off the sender's
            balance, and a transfer on the road cannot be cancelled. */}
        <p>{t('card.wh.transfer.dispatchHint')}</p>
        <p>{t('record.wh.tr.dispatch.text', { qty: qty(sent, locale), positions: lines.length, from, to })}</p>
        <p className="strong" style={{ color: 'var(--fg)' }}>
          {t('record.irreversible')}
        </p>
      </ConfirmModal>

      <ConfirmModal
        open={confirm === 'cancel'}
        icon="close"
        title={t('record.wh.tr.cancel.title', { number: transfer.number })}
        confirmLabel={t('card.wh.transfer.cancel')}
        pending={cancel.isPending}
        onCancel={() => setConfirm(null)}
        onConfirm={() => cancel.mutate()}
      >
        <p>{t('record.wh.tr.cancel.text')}</p>
        <p className="strong" style={{ color: 'var(--fg)' }}>
          {t('record.irreversible')}
        </p>
      </ConfirmModal>
    </div>
  )
}

/**
 * Строка перемещения. Непосчитанная — прочерк и «ожидает приёмки», а не ноль: ноль читался
 * бы как «ничего не доехало», то есть как претензия к перевозчику за то, чего никто не
 * проверял.
 */
function TransferRow({
  line,
  draft,
  name,
}: {
  line: TransferLine
  draft: boolean
  name: string | undefined
}) {
  const { t, locale } = useApp()
  const loss = transitLoss(line)
  let status: React.ReactNode
  if (draft) status = <span className="badge">{t('record.wh.tr.notSent')}</span>
  else if (!line.counted)
    status = (
      <span className="badge b-info">
        <Icon name="clock" />
        {t('record.wh.tr.waiting')}
      </span>
    )
  else if (loss > 0)
    status = (
      <span className="badge b-warning">
        <Icon name="alert" />
        {t('record.wh.tr.acceptedWithLoss')}
      </span>
    )
  else
    status = (
      <span className="badge b-success">
        <Icon name="check" />
        {t('card.wh.transfer.accepted')}
      </span>
    )
  return (
    <tr>
      <td className="wrap">
        <ProductCell id={line.productId} name={name} sku={line.sku} />
      </td>
      <td className="num">{qty(line.quantity, locale)}</td>
      <td className="num">{line.counted ? qty(line.received, locale) : <span className="muted">—</span>}</td>
      <td className="num">
        {!line.counted ? (
          <span className="muted">—</span>
        ) : loss > 0 ? (
          <span className="flag flag-danger">
            <Icon name="alert" />
            <span className="strong">{signedQty(-loss, locale)}</span>
          </span>
        ) : (
          <span className="muted">{qty(0, locale)}</span>
        )}
      </td>
      <td>
        <CellCode id={line.toCellId} code={line.toCellCode} />
      </td>
      <td>{status}</td>
    </tr>
  )
}
